<template>
  <v-data-table
    :sort-by="sortBy"
    density="compact"
    fixed-header
    :headers="headers"
    :items="diagnosticsWithName"
    items-per-page="-1"
    :style="`height: calc(100% - ${toolbarHeight})`"
  >
    <template #no-data><v-container class="font-weight-light">There are no problems.</v-container></template>
    <template #item="{ item }">
      <tr class="table-row" @click="revealElement(item)">
        <td>
          <v-list-item class="message-text pl-0">
            <v-icon :icon="getSeverityIcon(item.severity)" :color="getSeverityColor(item.severity)" size="small">
            </v-icon>
            {{ item.message }}
          </v-list-item>
        </td>
        <td>
          {{ item.elementName }}
        </td>
      </tr>
    </template>
    <template #bottom></template>
  </v-data-table>
</template>

<script setup lang="ts">
import { dia } from "@clientio/rappid";
import RappidContext from "../rappid/rappid-context";

import { DiagnosticSeverity, IdentifiableDiagnostic } from "../services/validation-service";
import { computed, inject } from "vue";
import { toolbarHeight } from "../utils/resizeCard";
import { VDataTable } from "vuetify/components";
import { findSubdiagramParent, updateVisibleStates } from "../subdiagram-viewer";
import { StateView } from "../mvc/state";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const compProps = defineProps<{
  diagnostics: Array<IdentifiableDiagnostic>;
  height: number;
}>();

const diagnosticsWithName = computed<Array<IdentifiableDiagnostic>>(() => {
  return compProps.diagnostics.map((el) => {
    const tempEl = { ...el };
    tempEl.elementName = getElementName(el.elementId);
    return tempEl;
  });
});

const sortBy: VDataTable["$props"]["sortBy"] = [{ key: "elementName", order: "asc" }];
const headers: VDataTable["$props"]["headers"] = [
  {
    title: "Message",
    align: "start",
    sortable: false,
    key: "message",
  },
  {
    title: "Element",
    sortable: true,
    key: "elementName",
    width: "20%",
  },
];

function getSeverityIcon(severity: DiagnosticSeverity) {
  if (severity === DiagnosticSeverity.ERROR) return "mdi-alert";
  if (severity === DiagnosticSeverity.WARNING) return "mdi-alert-circle";
  if (severity === DiagnosticSeverity.INFO) return "mdi-alert-circle-outline";
}
function getSeverityColor(severity: DiagnosticSeverity) {
  if (severity === DiagnosticSeverity.ERROR) return "error";
  if (severity === DiagnosticSeverity.WARNING) return "warning";
  if (severity === DiagnosticSeverity.INFO) return "information";
}

function getElementName(elementId: string) {
  const cell: dia.Cell = rappidContext.graph.getCells().find((c) => c.id === elementId);
  if (cell && cell["getName"] && cell["getName"]()) {
    return cell.get("type") + " - " + cell["getName"]();
  }
  return cell?.get("type") || "Statechart";
}
function revealElement(data: IdentifiableDiagnostic) {
  const cell = rappidContext.graph.getCells().find((c) => c.id === data.elementId);
  if (cell) {
    rappidContext.selection.collection.reset([cell]);
    rappidContext.selection.createSelectionBox(cell);
    rappidContext.paperScroller.scrollToContent(cell.getBBox());
    const parent = findSubdiagramParent(cell);
    const parentView = parent ? (parent.findView(rappidContext.paper) as StateView) : null;
    updateVisibleStates(rappidContext, parentView);
  }
}
</script>

<style scoped>
.problem-view-list :deep(td) {
  font-size: small !important;
}
.message-text {
  overflow: hidden;
  text-overflow: ellipsis;
  /*white-space: nowrap;*/
}

.table-row:hover td {
  background: rgba(var(--v-theme-background));
  cursor: pointer;
}
</style>
